/**
 * Admin Cover Art & DOCX Regeneration — Oct 2026
 * Upload cover images per book and re-generate the synopsis DOCX
 * with embedded covers.  Covers are stored in Emergent Object Storage.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CoverArtPanel() {
  const [books,       setBooks]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [uploading,   setUploading]   = useState({});   // book_id → bool
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/bookstore/synopsis-batch/all`);
      const all = [...(r.data.approved || []), ...(r.data.pending || [])];
      all.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      setBooks(all);
    } catch {
      toast.error('Could not load books.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (bookId, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [bookId]: true }));
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await axios.post(`${API}/api/admin/bookstore/books/${bookId}/cover`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Cover uploaded.');
      setBooks((prev) =>
        prev.map((b) => b.id === bookId ? { ...b, cover_front_url: r.data.cover_url } : b)
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading((u) => ({ ...u, [bookId]: false }));
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const r = await axios.post(`${API}/api/admin/synopses/regenerate`, {}, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Crown_and_Rose_Story_Synopses.docx';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('DOCX regenerated and downloaded.');
    } catch {
      toast.error('Regeneration failed — check backend logs.');
    } finally {
      setRegenerating(false);
    }
  };

  const filtered = books.filter((b) => {
    const q = search.toLowerCase();
    return !q || (b.title || '').toLowerCase().includes(q) || (b.author || '').toLowerCase().includes(q);
  });

  const withCover    = books.filter((b) => b.cover_front_url).length;
  const withSynopsis = books.filter((b) => b.back_copy).length;

  return (
    <div className="space-y-4 p-4">
      {/* Header row */}
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-gray-900">Cover Art &amp; DOCX</h2>
        <div className="flex gap-2 text-xs text-gray-500">
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">{withCover} covers uploaded</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5">{withSynopsis} synopses</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5">{books.length} total</span>
        </div>
        <div className="ml-auto flex gap-2">
          <button
            onClick={load}
            className="rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
            data-testid="covers-refresh-btn"
          >
            Refresh
          </button>
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            className="rounded-md bg-rose-700 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50 hover:bg-rose-800"
            data-testid="covers-regenerate-btn"
          >
            {regenerating ? 'Generating…' : 'Regenerate DOCX'}
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Covers are embedded into the DOCX at 2.5″ width. Accepted formats: JPEG, PNG, WebP.
        The DOCX is also saved to <code className="font-mono">/public/Crown_and_Rose_Story_Synopses.docx</code>.
      </p>

      <input
        type="search"
        placeholder="Filter by title or author…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full max-w-sm rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-rose-400 focus:outline-none"
        data-testid="covers-search"
      />

      {loading && <div className="text-sm text-gray-400">Loading…</div>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((b) => (
          <BookCoverCard
            key={b.id}
            book={b}
            uploading={!!uploading[b.id]}
            onUpload={(file) => handleUpload(b.id, file)}
          />
        ))}
        {!loading && filtered.length === 0 && (
          <div className="col-span-3 py-8 text-center text-sm text-gray-400">
            {search ? 'No books match your search.' : 'No books found.'}
          </div>
        )}
      </div>
    </div>
  );
}

function BookCoverCard({ book, uploading, onUpload }) {
  const inputRef = useRef(null);

  return (
    <div
      className="flex gap-3 rounded-lg border border-gray-100 bg-white p-3 shadow-sm"
      data-testid={`cover-card-${book.id}`}
    >
      {/* Cover thumbnail or placeholder */}
      <div
        className="h-20 w-14 shrink-0 overflow-hidden rounded border border-gray-100 bg-gray-50 flex items-center justify-center cursor-pointer"
        onClick={() => inputRef.current?.click()}
        title="Click to upload cover"
      >
        {book.cover_front_url ? (
          <img
            src={book.cover_front_url}
            alt={book.title}
            className="h-full w-full object-cover"
            data-testid={`cover-img-${book.id}`}
          />
        ) : (
          <span className="text-[10px] text-gray-300 text-center px-1">No cover</span>
        )}
      </div>

      {/* Info + actions */}
      <div className="flex flex-1 flex-col justify-between min-w-0">
        <div>
          <p className="truncate text-sm font-semibold text-gray-900">{book.title || book.id}</p>
          <p className="truncate text-xs text-gray-400">{book.author || '—'}</p>
          {book.back_copy && (
            <span className="mt-0.5 inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-700">
              Synopsis ready
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="mt-2 w-full rounded border border-gray-200 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          data-testid={`cover-upload-btn-${book.id}`}
        >
          {uploading ? 'Uploading…' : book.cover_front_url ? 'Replace cover' : 'Upload cover'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUpload(file);
            e.target.value = '';
          }}
          data-testid={`cover-input-${book.id}`}
        />
      </div>
    </div>
  );
}
